import { getRuntimeConfig } from '@/shared/config';
import { ApiError, createStepUpGrant, creditApi, type StepUpAction, type StepUpGrant } from '@/shared/http';

/**
 * Abre a sessão do step-up (discovery/03 §2.4): `POST {CREDIT_API_URL}/Auth/StartAuth` com
 * `{ username, password, poolId, cognitoClientId }` e `Authorization: Bearer ` vazio, como
 * `services/identity/index.ts:84-99` do legado. Devolve o `sessionId`. Nunca chamado por agente (U24).
 */
export async function startStepUpSession(
  password: string,
  options: { readonly username: string | undefined; readonly signal?: AbortSignal },
): Promise<string> {
  const config = getRuntimeConfig();
  try {
    const sessionId = await creditApi.post(
      '/Auth/StartAuth',
      { username: options.username, password, poolId: config.USER_POOL_ID, cognitoClientId: config.USER_CLIENT_ID },
      { authorization: 'empty-bearer', ...(options.signal ? { signal: options.signal } : {}) },
    );
    if (typeof sessionId !== 'string' || sessionId === '') {
      throw new ApiError({ kind: 'unknown', api: creditApi.api, payload: { type: 'error', code: 'STEP_UP_SESSION_INVALID' } });
    }
    return sessionId;
  } catch (error) {
    throw withoutRequest(error);
  }
}

/**
 * O `AxiosError` guarda o corpo enviado (`config.data`, com a senha) no `cause` do `ApiError`: quem
 * registrar o erro levaria a senha junto. Sai só o que a tela precisa (status, código, corpo da resposta).
 */
function withoutRequest(error: unknown): ApiError {
  const from = ApiError.from(error, creditApi.api);
  return new ApiError({
    kind: from.kind,
    payload: from.payload,
    api: creditApi.api,
    ...(from.status === undefined ? {} : { status: from.status }),
  });
}

/** Resposta do `POST /Auth/StepUp/Start` (`StepUpStartResponse` da CreditAPI). */
interface StepUpChallenge {
  readonly challengeId: string;
  readonly expiresAt: string;
}

/**
 * Primeira etapa do step-up novo (`AuthController.StepUpStartAsync`, `stepUp.ts:startStepUp` do legado):
 * `POST {CREDIT_API_URL}/Auth/StepUp/Start` com `{ password, action, resourceId }` e o Bearer da sessão.
 * Devolve o `challengeId` (validade de até 180 s). O erro sai sem o corpo enviado (a senha).
 */
export async function startStepUpChallenge(
  password: string,
  request: { readonly action: StepUpAction; readonly resourceId?: string | null | undefined },
  signal?: AbortSignal,
): Promise<StepUpChallenge> {
  try {
    const data = await creditApi.post<Partial<StepUpChallenge> | null>(
      '/Auth/StepUp/Start',
      { password, action: request.action, resourceId: request.resourceId ?? null },
      signal ? { signal } : {},
    );
    if (typeof data?.challengeId !== 'string' || data.challengeId === '') throw invalid('STEP_UP_CHALLENGE_INVALID');
    return { challengeId: data.challengeId, expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : '' };
  } catch (error) {
    throw withoutRequest(error);
  }
}

/**
 * Segunda etapa (`AuthController.StepUpVerifyAsync`, `stepUp.ts:verifyStepUp`): `POST /Auth/StepUp/Verify` com
 * `{ challengeId, code }`. Devolve a autorização de uso único (até 300 s), presa ao usuário, à ação e ao registro.
 */
export async function verifyStepUpChallenge(challengeId: string, code: string, signal?: AbortSignal): Promise<StepUpGrant> {
  try {
    const data = await creditApi.post<{ stepUpToken?: unknown; action?: unknown; resourceId?: unknown; expiresAt?: unknown } | null>(
      '/Auth/StepUp/Verify',
      { challengeId, code },
      signal ? { signal } : {},
    );
    if (typeof data?.stepUpToken !== 'string' || data.stepUpToken === '') throw invalid('STEP_UP_CHALLENGE_INVALID');
    return createStepUpGrant(data.stepUpToken, {
      action: typeof data.action === 'string' ? data.action : '',
      resourceId: typeof data.resourceId === 'string' ? data.resourceId : null,
      expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : '',
    });
  } catch (error) {
    throw withoutRequest(error);
  }
}

function invalid(code: string) {
  return new ApiError({ kind: 'unknown', api: creditApi.api, payload: { type: 'error', code } });
}
